// Pied de page. Données : ReactShell::Footer (chemins, année ; liens absents
// en mode annonces).
import AppLink from '@/components/ui/AppLink'
import { t } from '@/lib/i18n'

export type FooterData = {
  year: number
  featuresPath: string | null
  pricingPath: string
  aboutPath: string
  contactPath: string
  blogPath: string | null
  apiDocsPath: string | null
  businessPath: string | null
  termsPath: string
  termsOfSalePath: string
  privacyPath: string
  cookiesPath: string
  disclaimerPath: string
  legalNoticesPath: string
  methodologyPath: string
}

const LINK = 'text-muted text-decoration-none small'
// Sauts de ligne entre les éléments : sans effet à l'écran (tout est en
// flex), ils séparent les mots du texte du footer masqué (mode app, mobile).
const NL = '\n'

export default function Footer({ data }: { data: Record<string, any> }) {
  const f = data as FooterData
  return (
    <>
      <footer className="footer bg-dark text-light">
        {NL}
        <div className="container-fluid px-4">
          {NL}
          {/* Compact single-line footer for app pages */}
          {NL}
          <div className="footer-compact d-flex flex-wrap align-items-center justify-content-between py-2">
            {NL}
            <div className="d-flex align-items-center gap-3">
              {NL}
              <span className="text-muted small">&copy; {f.year} Northvane</span>
              {NL}
              <span className="text-muted d-none d-md-inline">|</span>
              {NL}
              <div className="footer-links d-none d-md-flex gap-2">
                {NL}
                {f.featuresPath && <><AppLink href={f.featuresPath} className={LINK}>{t('footer.features')}</AppLink>{NL}</>}
                <AppLink href={f.pricingPath} className={LINK}>{t('footer.pricing')}</AppLink>
                {NL}
                <AppLink href={f.aboutPath} className={LINK}>{t('footer.about')}</AppLink>
                {NL}
                <AppLink href={f.contactPath} className={LINK}>{t('footer.contact_us')}</AppLink>
                {NL}
                {f.blogPath && <><AppLink href={f.blogPath} className={LINK}>{t('footer.blog')}</AppLink>{NL}</>}
                {f.apiDocsPath && <><AppLink href={f.apiDocsPath} className={LINK} title={t('footer.api_from_pro_title')}>{t('footer.api_from_pro')}</AppLink>{NL}</>}
                {f.businessPath && <><AppLink href={f.businessPath} className={LINK}>{t('footer.business', { default: 'Business' })}</AppLink>{NL}</>}
              </div>
              {NL}
            </div>
            {NL}
            <div className="d-flex align-items-center gap-3">
              {NL}
              <div className="footer-legal d-none d-lg-flex gap-2">
                {NL}
                <AppLink href={f.termsPath} className={LINK}>{t('footer.terms')}</AppLink>
                {NL}
                <AppLink href={f.termsOfSalePath} className={LINK}>{t('footer.terms_of_sale')}</AppLink>
                {NL}
                <AppLink href={f.privacyPath} className={LINK}>{t('footer.privacy')}</AppLink>
                {NL}
                <AppLink href={f.cookiesPath} className={LINK}>{t('footer.cookies')}</AppLink>
                {NL}
                <AppLink href={f.disclaimerPath} className={LINK}>{t('footer.disclaimer')}</AppLink>
                {NL}
                <AppLink href={f.methodologyPath} className={LINK}>{t('footer.methodology')}</AppLink>
                {NL}
                <AppLink href={f.legalNoticesPath} className={LINK}>{t('footer.legal_notices')}</AppLink>
                {NL}
              </div>
              {NL}
              {/* GDPR Compliance Badge */}
              {NL}
              <span className="gdpr-badge d-none d-md-inline-flex align-items-center gap-1 small">
                {NL}
                <i className="fas fa-shield-alt text-success"></i>
                {NL}
                <span className="text-muted">{t('footer.gdpr_compliant', { default: 'GDPR Compliant' })}</span>
                {NL}
              </span>
              {NL}
              <div className="social-links d-flex gap-2">
                {NL}
                <a href="https://www.instagram.com/northvane.app/" className="text-muted" target="_blank" rel="noopener" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                {NL}
                <a href="https://www.facebook.com/northvane/" className="text-muted" target="_blank" rel="noopener" aria-label="Facebook"><i className="fab fa-facebook"></i></a>
                {NL}
                <a href="https://x.com/NorthvaneApp" className="text-muted" target="_blank" rel="noopener" aria-label="X"><i className="fab fa-x-twitter"></i></a>
                {NL}
                <a href="https://www.linkedin.com/company/northvane" className="text-muted" target="_blank" rel="noopener" aria-label="LinkedIn"><i className="fab fa-linkedin"></i></a>
                {NL}
              </div>
              {NL}
            </div>
            {NL}
          </div>
          {NL}
        </div>
        {NL}
      </footer>

      <style>{`
.footer {
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.footer-compact {
  min-height: 40px;
}

.footer a {
  color: #64748b !important;
  transition: color 0.2s ease;
  font-size: 0.75rem;
}

.footer a:hover {
  color: #f7f6f4 !important;
}

.social-links a {
  font-size: 0.85rem;
  color: #64748b !important;
}

.social-links a:hover {
  color: #f7f6f4 !important;
}

.footer .text-muted {
  color: #64748b !important;
}

.footer-links a,
.footer-legal a {
  padding: 0 0.25rem;
}

/* Landing pages get a bigger footer */
.landing-page .footer,
body:not(.has-sidebar) .footer {
  padding: 2rem 0;
}

.landing-page .footer .footer-compact,
body:not(.has-sidebar) .footer .footer-compact {
  flex-direction: column;
  gap: 1rem;
  text-align: center;
}

.landing-page .footer .footer-links,
.landing-page .footer .footer-legal,
body:not(.has-sidebar) .footer .footer-links,
body:not(.has-sidebar) .footer .footer-legal {
  display: flex !important;
  justify-content: center;
  flex-wrap: wrap;
}

@media (max-width: 767px) {
  .footer-compact {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
    padding: 0.75rem 0 !important;
  }

  .footer-links,
  .footer-legal {
    display: flex !important;
    justify-content: center;
    flex-wrap: wrap;
  }
}
`}</style>
    </>
  )
}
